/**
 * connectedAppsStore — session-cached "which apps are connected" reader.
 *
 * Chips are grounded against LIVE connection state at render time
 * (capabilityGrounding.ts), and several surfaces may ask per render — so this
 * wraps ComposioClient.getConnectedAccounts() behind a short TTL + in-flight
 * dedupe. Contract mirrors the grounding module's fail-open rule: `null`
 * means "state unknown" (never fetched / fetch failed) and callers must not
 * ground against it.
 *
 * Custom (browser-signin) connections count too (2026-08-19): the backend
 * accountResolver has always fed `custom-*` docs with status 'connected'
 * into the prompt, while this client roster read Composio accounts only —
 * so a finished browser sign-in stayed invisible to chips and the web
 * orchestrator kept re-offering to connect the same system. Both the
 * `custom-<slug>` id and the plain lowercased name are included so
 * name-based grounding matches.
 *
 * `invalidateConnectedAppsCache()` is called when an in-chat connect completes
 * (composio.connection_complete) so a freshly-connected app becomes usable in
 * suggestions immediately — the same "instant usability" directive that has
 * the connect card trigger a server-side capability reconcile.
 */

import { getConnectedAccounts, listCustomConnections } from '../composio/ComposioClient';

const TTL_MS = 2 * 60 * 1000;

let cached: { slugs: string[]; ts: number } | null = null;
let inflight: Promise<string[] | null> | null = null;

export function invalidateConnectedAppsCache(): void {
  cached = null;
  inflight = null;
}

/** Lowercased ACTIVE toolkit slugs (plus connected custom slugs/names), or
 *  null when the state is unknowable. */
export async function getConnectedAppSlugs(): Promise<string[] | null> {
  if (cached && Date.now() - cached.ts < TTL_MS) return cached.slugs;
  if (inflight) return inflight;

  inflight = (async (): Promise<string[] | null> => {
    try {
      // Custom connections are best-effort ADDITIVE — a failed custom fetch
      // must not blank the Composio roster (fail open, never smaller).
      const [accounts, customs] = await Promise.all([
        getConnectedAccounts(),
        listCustomConnections().catch(() => []),
      ]);
      const slugs = accounts
        .filter((a) => (a.status || '').toUpperCase() === 'ACTIVE')
        .map((a) => (a.toolkit?.slug || '').toLowerCase())
        .filter((s) => s.length > 0);
      for (const c of customs) {
        if (String(c.status || '') !== 'connected') continue;
        if (c.slug) slugs.push(c.slug.toLowerCase());
        const name = String(c.name || '').trim().toLowerCase();
        if (name) slugs.push(name);
      }
      cached = { slugs, ts: Date.now() };
      return slugs;
    } catch {
      return null; // unknown — callers ground nothing (fail open)
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
